import axios from 'axios';

// Determine the base URL based on environment
const getBaseUrl = () => {
  // Check if running in browser or server
  if (typeof window !== 'undefined') {
    // In production (Vercel), use the production API URL
    if (window.location.hostname !== 'localhost' && 
        !window.location.hostname.includes('127.0.0.1')) {
      console.log('Using production API URL: https://api.tuttogym.com/api');
      return 'https://api.tuttogym.com/api';
    }
  }
  // For local development, use localhost with port 3001
  console.log('Using development API URL: http://localhost:3001/api');
  return 'http://localhost:3001/api';
};

// Base API URL 
const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  // Adding timeout to prevent hanging requests
  timeout: 10000 // 10 seconds
});

// Log request and response for debugging
apiClient.interceptors.request.use(
  config => {
    console.log(`🚀 API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`, config.data);
    return config;
  },
  error => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  response => {
    console.log(`✅ API Response: ${response.status} from ${response.config.url}`, response.data);
    return response;
  },
  error => {
    console.error(`❌ API Response Error: ${error.response?.status || 'Unknown'} from ${error.config?.url}`, error.response?.data);
    return Promise.reject(error);
  }
);

// Export apiClient for direct use in components
export { apiClient };

// Check-in related API calls
export const processCheckIn = async (checkInData) => {
  try {
    const response = await apiClient.post('/check-ins', checkInData);
    
    // Trigger dashboard refresh if available
    if (typeof window.dashboardRefresh === 'function') {
      window.dashboardRefresh();
    }
    
    // Dispatch custom event for real-time updates
    const event = new CustomEvent('newCheckIn', { detail: checkInData });
    window.dispatchEvent(event);
    
    return response.data;
  } catch (error) {
    console.error('Error processing check-in:', error);
    return {
      success: false,
      message: 'Error processing check-in: ' + (error.response?.data?.message || error.message)
    };
  }
};

export const getCheckInsByUserEmail = async (userEmail) => {
  try {
    const response = await apiClient.get(`/check-ins?email=${encodeURIComponent(userEmail)}`);
    return response.data;
  } catch (error) {
    console.error('Error getting check-ins by user email:', error);
    return [];
  }
};

// Authentication related API calls
export const login = async (email, password, userType = null) => {
  try {
    console.log('Logging in with email:', email);
    console.log('User type from parameter:', userType);
    
    const response = await apiClient.post('/auth/login', { email, password });
    console.log('Raw login API response status:', response.status);
    
    const data = response.data;
    console.log('Login response data:', data);
    
    // Store authentication data in localStorage
    if (data.success && data.token) {
      console.log('Login successful, storing auth data for user:', data.user.id);
      console.log('User role from response:', data.user.role);
      
      localStorage.setItem('authToken', data.token);
      
      // Set user role based on provided type or default
      const role = userType || data.user.role || 'customer';
      console.log('Final user role being stored:', role);
      
      localStorage.setItem('userRole', role);
      localStorage.setItem('userEmail', data.user.email);
      localStorage.setItem('userId', data.user.id.toString());
      
      // Store user-specific data based on role
      if (role === 'customer') {
        console.log('Storing customer data:', {
          firstName: data.user.firstName || '',
          lastName: data.user.lastName || ''
        });
        localStorage.setItem('firstName', data.user.firstName || '');
        localStorage.setItem('lastName', data.user.lastName || '');
      } else if (role === 'company') {
        console.log('Storing company data:', {
          companyName: data.user.companyName || ''
        });
        localStorage.setItem('companyName', data.user.companyName || '');
      } else if (role === 'partner') {
        console.log('Storing partner data:', {
          partnerName: data.user.partnerName || '',
          location: data.user.location || ''
        });
        localStorage.setItem('partnerName', data.user.partnerName || '');
        localStorage.setItem('location', data.user.location || '');
      }
      
      localStorage.setItem('allowNotifications', 'true');
      console.log('Auth data storage complete. Token stored with length:', data.token.length);
    } else {
      console.warn('Login unsuccessful or missing token', { 
        success: data.success, 
        hasToken: !!data.token 
      });
    }
    
    return data;
  } catch (error) {
    console.error('Login error:', error);
    console.error('Login error details:', { 
      message: error.message,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userId');
  localStorage.removeItem('firstName');
  localStorage.removeItem('lastName');
  localStorage.removeItem('companyName');
  localStorage.removeItem('partnerName');
  localStorage.removeItem('location');
  localStorage.removeItem('allowNotifications');
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('authToken');
};

export const getToken = () => {
  return localStorage.getItem('authToken');
};

export const getUserType = () => {
  return localStorage.getItem('userRole') || 'customer';
};

export const getCurrentUser = () => {
  if (!isAuthenticated()) {
    return null;
  }
  
  const role = getUserType();
  const userData = {
    id: localStorage.getItem('userId'),
    email: localStorage.getItem('userEmail'),
    role: role
  };
  
  // Add role-specific data
  if (role === 'customer') {
    userData.firstName = localStorage.getItem('firstName');
    userData.lastName = localStorage.getItem('lastName');
  } else if (role === 'company') {
    userData.companyName = localStorage.getItem('companyName');
  } else if (role === 'partner') {
    userData.partnerName = localStorage.getItem('partnerName');
    userData.location = localStorage.getItem('location');
    userData.fitnessCenterId = localStorage.getItem('fitnessCenterId');
  }
  
  return userData;
};

// Auto-login after registration
export const autoLoginAfterRegistration = (userData, userType) => {
  const userId = userData.id || '';
  const email = userData.email || '';
  
  console.log('Auto-login for user data:', userData);
  console.log('User type:', userType);
  
  // Create a more robust token similar to login endpoint
  const token = 'auth-token-' + Date.now() + '-' + Math.random().toString(36).substring(2);
  
  // Store auth token
  localStorage.setItem('authToken', token);
  localStorage.setItem('userRole', userType);
  localStorage.setItem('userEmail', email);
  localStorage.setItem('userId', userId.toString());
  
  // Store user-specific data
  if (userType === 'customer') {
    localStorage.setItem('firstName', userData.firstName || userData.firstname || '');
    localStorage.setItem('lastName', userData.lastName || userData.lastname || '');
  } else if (userType === 'company' || userType === 'business') {
    localStorage.setItem('companyName', userData.companyName || userData.companyname || '');
  } else if (userType === 'partner') {
    localStorage.setItem('partnerName', userData.partnerName || userData.partnername || '');
    localStorage.setItem('location', userData.location || '');
  }
  
  localStorage.setItem('allowNotifications', 'true');
  
  console.log('Auto-login completed. Token stored with length:', token.length);
  
  return {
    success: true,
    userId: userId,
    userType: userType,
    token: token
  };
};

// Interested company related API calls
export const saveInterestedCompany = async (companyData) => {
  try {
    const response = await apiClient.post('/interested-company', companyData);
    return response.data;
  } catch (error) {
    console.error('Error saving company data:', error);
    return {
      success: false,
      message: 'Error saving company data: ' + (error.response?.data?.message || error.message)
    };
  }
};

// Partner related API calls
export const saveInterestedPartner = async (partnerData) => {
  try {
    const response = await apiClient.post('/interested-partner', partnerData);
    return response.data;
  } catch (error) {
    console.error('Error saving partner data:', error);
    return {
      success: false,
      message: 'Error saving partner data: ' + (error.response?.data?.message || error.message)
    };
  }
};

// Individual user signup related API calls
export const saveUserSignup = async (userData) => {
  try {
    const response = await apiClient.post('/user-signup', userData);
    return response.data;
  } catch (error) {
    console.error('Error saving user signup data:', error);
    return {
      success: false,
      message: 'Error saving user data: ' + (error.response?.data?.message || error.message)
    };
  }
};

// Fitness centers related API calls
export const getActivityTypes = async () => {
  try {
    const response = await apiClient.get('/fitness-centers/activity-types');
    return response.data;
  } catch (error) {
    console.error('Error getting activity types:', error);
    return {
      success: false,
      data: [],
      message: 'Error fetching activity types: ' + (error.response?.data?.message || error.message)
    };
  }
};

export const searchFitnessCenters = async (params) => {
  try {
    // Build query string from params
    const queryParams = new URLSearchParams();
    
    if (params.city) {
      queryParams.append('city', params.city);
    }
    
    if (params.address) {
      queryParams.append('address', params.address);
    }
    
    if (params.postalCode) {
      queryParams.append('postalCode', params.postalCode);
    }
    
    // Handle activities as comma-separated string if it's an array
    if (params.activityType && Array.isArray(params.activityType) && params.activityType.length > 0) {
      queryParams.append('activityType', params.activityType.join(','));
    } else if (params.activityType) {
      queryParams.append('activityType', params.activityType);
    }
    
    if (params.page) {
      queryParams.append('page', params.page.toString());
    }
    
    if (params.pageSize) {
      queryParams.append('pageSize', params.pageSize.toString());
    }
    
    const response = await apiClient.get(`/fitness-centers/search?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error searching fitness centers:', error);
    return {
      success: false,
      data: [],
      pagination: { total_items: 0, total_pages: 0, current_page: 1, page_size: 9 },
      message: 'Error searching fitness centers: ' + (error.response?.data?.message || error.message)
    };
  }
};

export const getCities = async () => {
  try {
    const response = await apiClient.get('/fitness-centers/cities');
    return {
      success: true,
      data: response.data.data || []
    };
  } catch (error) {
    console.error('Error getting cities:', error);
    return {
      success: false,
      data: [],
      message: 'Error fetching cities: ' + (error.response?.data?.message || error.message)
    };
  }
};

export const getPostalCodes = async () => {
  try {
    const response = await apiClient.get('/fitness-centers/postal-codes');
    return {
      success: true,
      data: response.data.data || []
    };
  } catch (error) {
    console.error('Error getting postal codes:', error);
    return {
      success: false,
      data: [],
      message: 'Error fetching postal codes: ' + (error.response?.data?.message || error.message)
    };
  }
};

// Registration API calls
export const registerCustomer = async (customerData) => {
  try {
    console.log('Sending customer registration request with data:', customerData);
    
    const response = await apiClient.post('/register/customer', customerData);
    console.log('Raw customer registration response:', response);
    console.log('Customer registration response data:', response.data);
    
    if (response.data.success) {
      // Auto-login after successful registration
      console.log('Auto-login after customer registration for user data:', response.data.data);
      const autoLoginResult = autoLoginAfterRegistration(response.data.data, 'customer');
      console.log('Auto-login result:', autoLoginResult);
      
      // Add token to the response
      return {
        ...response.data,
        token: autoLoginResult.token
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error registering customer:', error);
    console.error('Error details:', { 
      message: error.message,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });
    throw error;
  }
};

export const registerCompany = async (companyData) => {
  try {
    console.log('Sending company registration request with data:', companyData);
    
    const response = await apiClient.post('/register/company', companyData);
    console.log('Raw company registration response:', response);
    console.log('Company registration response data:', response.data);
    
    if (response.data.success) {
      // Auto-login after successful registration
      console.log('Auto-login after company registration for user data:', response.data.data);
      const autoLoginResult = autoLoginAfterRegistration(response.data.data, 'company');
      console.log('Auto-login result:', autoLoginResult);
      
      // Add token to the response
      return {
        ...response.data,
        token: autoLoginResult.token
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error registering company:', error);
    console.error('Error details:', { 
      message: error.message,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });
    throw error;
  }
};

export const registerPartner = async (partnerData) => {
  try {
    console.log('Sending partner registration request with data:', partnerData);
    
    const response = await apiClient.post('/register/partner', partnerData);
    console.log('Raw partner registration response:', response);
    console.log('Partner registration response data:', response.data);
    
    if (response.data.success) {
      // Auto-login after successful registration
      console.log('Auto-login after partner registration for user data:', response.data.data);
      const autoLoginResult = autoLoginAfterRegistration(response.data.data, 'partner');
      console.log('Auto-login result:', autoLoginResult);
      
      // Add token to the response
      return {
        ...response.data,
        token: autoLoginResult.token
      };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error registering partner:', error);
    console.error('Error details:', { 
      message: error.message,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });
    throw error;
  }
};

// Verify if a fitness center exists and find similar ones
export const verifyFitnessCenter = async (name, address) => {
  try {
    console.log('Enviando petición para verificar fitness center:', { name, address });
    
    // Ensure name and address are properly trimmed
    const cleanName = name ? name.trim() : '';
    const cleanAddress = address ? address.trim() : '';
    
    const response = await apiClient.post('/fitness-centers/verify', { 
      name: cleanName, 
      address: cleanAddress 
    });
    
    console.log('Respuesta de verificación:', {
      status: response.data.status,
      count: response.data.data?.length || 0
    });
    
    return response.data;
  } catch (error) {
    console.error('Error verifying fitness center:', error);
    return {
      status: 'error',
      message: 'Error verifying fitness center: ' + (error.response?.data?.message || error.message)
    };
  }
};

// Update partner profile with fitness center information
export const updatePartnerProfile = async (partnerId, fitnessData = null) => {
  try {
    const response = await apiClient.post('/partners/update-profile', {
      partnerId,
      fitnessData
    });
    
    // Update local storage with profile completion info if available
    if (response.data.status === 'success') {
      localStorage.setItem('profileComplete', 'true');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error updating partner profile:', error);
    return {
      status: 'error',
      message: 'Error updating partner profile: ' + (error.response?.data?.message || error.message)
    };
  }
};

// Check if partner profile is complete
export const isPartnerProfileComplete = () => {
  const role = getUserType();
  if (role !== 'partner') return true;
  
  // Check if partner profile is marked as complete
  return localStorage.getItem('profileComplete') === 'true';
};

// Get fitness center by ID
export const getFitnessCenterById = async (id) => {
  try {
    const response = await apiClient.get(`/fitness-centers/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error getting fitness center:', error);
    return null;
  }
};

// Get partner check-ins
export const getPartnerCheckIns = async (partnerId) => {
  try {
    // If we're in development mode and this is for partner@example.com, return empty data
    const currentUser = getCurrentUser();
    if (currentUser && currentUser.email === 'partner@example.com') {
      console.log('Returning empty check-ins data for partner@example.com');
      return {
        status: 'success',
        message: 'Check-ins retrieved successfully',
        data: {
          today: [],
          pending: [],
          accepted: []
        }
      };
    }
    
    const response = await apiClient.get(`/partners/${partnerId}/check-ins`);
    return response.data;
  } catch (error) {
    console.error('Error getting partner check-ins:', error);
    return {
      status: 'error',
      message: 'Error getting partner check-ins: ' + (error.response?.data?.message || error.message),
      data: {
        today: [],
        pending: [],
        accepted: []
      }
    };
  }
};

// Update check-in status (accept/reject)
export const updateCheckInStatus = async (checkInId, status, comments = '') => {
  console.log(`🚀 API Call: Updating check-in ID ${checkInId} status to "${status}" with comments: "${comments}"`);
  
  try {
    console.log(`Sending POST request to: /check-ins/${checkInId}/status`);
    console.log('Request payload:', { status, comments });
    
    const response = await apiClient.post(`/check-ins/${checkInId}/status`, { 
      status,
      comments 
    });
    
    console.log('✅ Status update API response:', response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ Error updating check-in status to ${status}:`, error);
    console.error('Error details:', {
      message: error.message,
      responseStatus: error.response?.status,
      responseData: error.response?.data,
      requestUrl: `/check-ins/${checkInId}/status`,
      requestPayload: { status, comments }
    });
    
    return {
      status: 'error',
      message: `Error updating check-in status: ${error.response?.data?.message || error.message}`
    };
  }
};

// Get partner profile information
export const getPartnerProfile = async (partnerId) => {
  try {
    const response = await apiClient.get(`/partners/${partnerId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting partner profile:', error);
    return {
      status: 'error',
      message: 'Error getting partner profile: ' + (error.response?.data?.message || error.message),
      data: null
    };
  }
};

// Update partner profile fields
export const updatePartnerProfileFields = async (partnerId, fitnessCenterId, updateFields) => {
  try {
    const payload = {
      fitnessCenterId,
      ...updateFields
    };
    
    const response = await apiClient.put(`/partners/${partnerId}`, payload);
    
    // Update local storage with profile completion info if available
    if (response.data.status === 'success') {
      localStorage.setItem('profileComplete', 'true');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error updating partner profile fields:', error);
    return {
      status: 'error',
      message: 'Error updating partner profile: ' + (error.response?.data?.message || error.message)
    };
  }
}; 